/* eslint-disable max-len */
/**
 * UploadActions — primary CTA + skip link styled to match the onboarding
 * and renewal screens. Use these in upload and add-item flows so every
 * "Continue / Save / Add" action and every "Skip this step" link looks
 * identical across the app.
 *
 * Behaviour mirrors TdButton's prop shape (label, onClick, isDisabled,
 * type, data-cy) so swapping a TdButton call site over is a one-line
 * change. Don't replace TdButton globally — only on the upload / add /
 * onboarding flows where the design calls for the round purple style.
 */
import React from 'react';

type PrimaryProps = {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  isDisabled?: boolean;
  className?: string;
  // eslint-disable-next-line react/require-default-props
  'data-cy'?: string;
};

const BUTTON_STYLE: React.CSSProperties = {
  background: '#AB72ED',
  boxShadow: '0px 2px 10px 0px #18006430',
  fontFamily: 'Grift, sans-serif',
  fontWeight: 500,
  fontSize: 18,
  lineHeight: '24px',
  letterSpacing: 0.5,
};

const DISABLED_STYLE: React.CSSProperties = {
  ...BUTTON_STYLE,
  background: '#CDC4D7',
  cursor: 'not-allowed',
  boxShadow: 'none',
};

export const PrimaryActionButton: React.FC<PrimaryProps> = ({
  label, onClick, type = 'button', isDisabled = false, className, ...rest
}) => (
  <button
    type={type === 'submit' ? 'submit' : 'button'}
    onClick={onClick}
    disabled={isDisabled}
    data-cy={(rest as { 'data-cy'?: string })['data-cy']}
    className={`w-full py-4 text-white rounded-full ${className || ''}`}
    style={isDisabled ? DISABLED_STYLE : BUTTON_STYLE}
  >
    {label}
  </button>
);

type SkipProps = {
  label?: string;
  onClick: () => void;
  className?: string;
  // eslint-disable-next-line react/require-default-props
  'data-cy'?: string;
};

const SKIP_STYLE: React.CSSProperties = {
  fontFamily: 'Grift, sans-serif',
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: '#573A78',
};

export const SkipStepLink: React.FC<SkipProps> = ({
  label = 'SKIP THIS STEP', onClick, className, ...rest
}) => (
  <button
    type="button"
    onClick={onClick}
    data-cy={(rest as { 'data-cy'?: string })['data-cy']}
    className={className}
    style={SKIP_STYLE}
  >
    {label}
  </button>
);
